import { parseQuery } from '@calendly/http-tools'

import { setGetParams } from 'actions/core/navigation'
import { isValidUUID } from 'shared/utils/uuid'

import {
  clearStoredAiRailThreadId,
  getStoredAiRailThreadId,
  setStoredAiRailThreadId,
} from './ai_rail_thread_storage'

export { clearStoredAiRailThreadId, setStoredAiRailThreadId }

/**
 * Clears meetings list selection query params when present — same shape as
 * `clearActiveMeeting` in meetings_page.js.
 *
 * Call before {@link useRightRail}'s `close()` when leaving the AI chat rail so the meetings
 * page does not re-open `meeting_details` from `selected` / `share_id`.
 *
 * @param {import('redux').Dispatch} dispatch
 * @param {string} search - `location.search` from react-router
 */
export const clearMeetingsSelectionFromUrlIfPresent = (dispatch, search) => {
  const { selected, share_id: shareId } = parseQuery(search)
  const hadSelection = (selected != null && selected !== '') || (shareId != null && shareId !== '')
  if (hadSelection) {
    dispatch(setGetParams({ selected: null, share_id: null }))
  }
}

/** Pane props round-trip through URL query encoding as the string "true". */
export const isForceNewChatEnabled = (value) => (
  value === true || value === 'true'
)

/**
 * Clears Callie onboarding query params when present.
 *
 * Call before {@link useRightRail}'s `close()` when leaving the AI chat rail so a later
 * reopen via {@link openAiChatRailPane} does not keep showing Callie onboarding from a
 * stale `?onboarding=callie` (and optional `&reset=1`).
 *
 * @param {import('redux').Dispatch} dispatch
 * @param {string} search - `location.search` from react-router
 */
export const clearCallieOnboardingFromUrlIfPresent = (dispatch, search) => {
  const { onboarding, reset } = parseQuery(search)
  const hadOnboardingParams = (
    (onboarding != null && onboarding !== '')
    || (reset != null && reset !== '')
  )
  if (hadOnboardingParams) {
    dispatch(setGetParams({ onboarding: null, reset: null }))
  }
}

/**
 * Merge explicit pane props with a stored rail thread id when props omit a valid threadId.
 * When `forceNewChat` is set, never hydrate from session storage and drop any threadId
 * so deep links always open a fresh chat with no history.
 * @param {Object} [props]
 * @returns {Object | undefined} props for `openOrReplace('ai_chat', …)` / `replaceAndClearStack`
 */
export const mergeAiChatPanePropsWithStoredThread = (props = {}) => {
  if (isForceNewChatEnabled(props.forceNewChat)) {
    // Fresh chat: never hydrate from session; drop any threadId (valid or invalid).
    const { threadId: _threadId, ...rest } = props
    return Object.keys(rest).length > 0 ? rest : undefined
  }

  const threadId = isValidUUID(props.threadId)
    ? props.threadId
    : getStoredAiRailThreadId()

  if (!threadId) {
    return Object.keys(props).length > 0 ? props : undefined
  }

  return { ...props, threadId }
}

/**
 * @param {(name: string, props?: Object) => void} openFn — `openOrReplace` or `replaceAndClearStack`
 * @param {Object} [props]
 */
export const openAiChatRailPane = (openFn, props) => {
  const merged = mergeAiChatPanePropsWithStoredThread(props ?? {})
  if (merged) {
    openFn('ai_chat', merged)
  } else {
    openFn('ai_chat')
  }
}

/**
 * Opens the Ask Callie rail in expanded (full-screen) mode and sets
 * `?onboarding=callie` so ChatApp shows Callie onboarding.
 * Open the pane first so `setGetParams` merges onto a URL that already has `pane=ai_chat`.
 *
 * @param {(name: string, props?: Object) => void} openFn — `openOrReplace` or `replaceAndClearStack`
 * @param {import('redux').Dispatch} dispatch
 * @param {Object} [props]
 */
export const openAiChatRailPaneWithCallieOnboarding = (openFn, dispatch, props) => {
  openAiChatRailPane(openFn, { ...props, railExpanded: true })
  dispatch(setGetParams({ onboarding: 'callie' }))
}

/** Persist pane thread id before closing so reopen can resume (close does not clear storage). */
const persistAiRailThreadIdBeforeClose = (current) => {
  const threadId = current?.paneProps?.threadId
  if (isValidUUID(threadId)) {
    setStoredAiRailThreadId(threadId)
  }
}

/**
 * @param {Function} closeFn
 * @param {Object | null} current — `useRightRail().current`
 */
export const closeAiChatRailPane = (closeFn, current) => {
  persistAiRailThreadIdBeforeClose(current)
  closeFn()
}
